import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Camera, Upload, Trash2, Link as LinkIcon, Image as ImageIcon,
  User, Check, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Helper kompresi gambar lokal via Canvas ke format Base64 (max 400x400)
function resizeAndCompressImage(file, maxSize = 400, quality = 0.88) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('File yang dipilih bukan gambar yang valid.'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Gagal membaca file gambar.'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Gagal memproses gambar.'));
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Export sebagai JPEG terkompresi
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const EditProfilDialog = ({ open, onOpenChange, user, onSave }) => {
  const [name, setName] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [avatar, setAvatar] = useState('');
  const [useUrlMode, setUseUrlMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && user) {
      setName(user.name || '');
      setRoleLabel(user.roleLabel || '');
      setAvatar(user.avatar || '');
      setUseUrlMode(false);
    }
  }, [open, user]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Batasi ukuran file mentah sebelum kompresi (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Ukuran file terlalu besar',
        description: 'Maksimal ukuran foto adalah 10 MB.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploadingImage(true);
      const compressedDataUrl = await resizeAndCompressImage(file, 400, 0.88);
      setAvatar(compressedDataUrl);
      toast({
        title: 'Foto berhasil dimuat',
        description: 'Foto siap disimpan sebagai foto profil baru Anda.',
      });
    } catch (err) {
      toast({
        title: 'Gagal memproses gambar',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = () => {
    setAvatar('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({
        title: 'Nama wajib diisi',
        description: 'Silakan masukkan nama lengkap Anda.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        roleLabel: roleLabel.trim() || undefined,
        avatar: avatar || '',
      };
      const ok = await onSave(payload);
      if (ok) {
        onOpenChange(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-xl font-black text-gray-950 flex items-center gap-2">
            <User className="w-5 h-5 text-orange-600" />
            Edit Profil
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 font-medium">
            Perbarui nama, peran, dan foto profil akun Anda.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* BAGIAN UPLOAD FOTO PROFIL */}
          <div className="flex flex-col items-center justify-center p-4 bg-orange-50/50 rounded-2xl border border-orange-100/80">
            <div className="relative group">
              {avatar ? (
                <img
                  src={avatar}
                  alt={name || 'Avatar'}
                  className="w-24 h-24 rounded-full object-cover shadow-sm ring-4 ring-orange-400/30 border-2 border-white"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop';
                  }}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-black text-2xl ring-4 ring-orange-200/50">
                  {name ? name.charAt(0).toUpperCase() : 'U'}
                </div>
              )}

              {/* Overlay camera trigger */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                title="Pilih Foto Baru"
                className="absolute bottom-0 right-0 p-2 rounded-full bg-orange-600 text-white shadow-md hover:bg-orange-700 transition scale-90 hover:scale-100"
              >
                {uploadingImage ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Camera className="w-3.5 h-3.5" />
                )}
              </button>
            </div>

            {/* Input File Tersembunyi */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
            />

            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="h-8 text-xs font-bold gap-1.5 border-orange-200 text-orange-800 hover:bg-orange-100/70 rounded-xl"
              >
                <Upload className="w-3.5 h-3.5 text-orange-600" />
                {uploadingImage ? 'Memproses...' : 'Upload Gambar'}
              </Button>

              {avatar && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleRemoveAvatar}
                  className="h-8 text-xs font-semibold text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Hapus
                </Button>
              )}
            </div>

            {/* Toggle Mode URL */}
            <button
              type="button"
              onClick={() => setUseUrlMode(!useUrlMode)}
              className="text-[11px] font-bold text-gray-400 hover:text-orange-600 mt-2 flex items-center gap-1 transition"
            >
              <LinkIcon className="w-3 h-3" />
              <span>{useUrlMode ? 'Sembunyikan Input URL' : 'Atau gunakan link URL foto'}</span>
            </button>

            {useUrlMode && (
              <div className="w-full mt-2.5">
                <Input
                  type="text"
                  value={avatar}
                  onChange={(e) => setAvatar(e.target.value)}
                  placeholder="https://example.com/foto.jpg"
                  className="h-8 text-xs font-medium rounded-xl bg-white border-orange-200"
                />
              </div>
            )}
          </div>

          {/* INPUT FORM NAMA & ROLE */}
          <div className="space-y-3.5">
            <div>
              <Label className="text-xs font-extrabold text-gray-700">Nama Lengkap *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nama lengkap Anda..."
                className="mt-1 rounded-xl h-10 font-bold text-xs"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-extrabold text-gray-700">Label Role / Jabatan</Label>
              <Input
                value={roleLabel}
                onChange={(e) => setRoleLabel(e.target.value)}
                placeholder="mis. Super Admin / Admin Wilayah"
                className="mt-1 rounded-xl h-10 font-medium text-xs"
              />
            </div>

            <div className="p-2.5 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-between text-xs">
              <span className="text-gray-500 font-medium">Username Login:</span>
              <span className="font-mono font-bold text-gray-900 bg-white px-2 py-0.5 rounded border border-gray-200">
                @{user?.username}
              </span>
            </div>
          </div>

          <DialogFooter className="pt-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="rounded-xl font-bold text-xs h-9"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={loading || uploadingImage}
              className="rounded-xl font-bold text-xs h-9 bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                'Simpan Perubahan'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfilDialog;

